import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVerifyEmail, useResendVerificationCode } from '../../hooks/useUserQueries';
import { useLogin } from '../../hooks/useAuthQueries';
import { useAuth } from '../../context/AuthContext';
import { ROUTES, STORAGE_KEYS } from '../../utils/constants';
import Button from '../common/Button';
import './EmailVerification.css';

const EmailVerification = ({ email, password, onVerified }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuth();
  const verifyEmailMutation = useVerifyEmail();
  const resendCodeMutation = useResendVerificationCode();
  // Use silent login for auto-login during signup (no toast notification)
  const loginMutation = useLogin({ silent: true });

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
    setSuccess('');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      // Step 1: Verify email
      await verifyEmailMutation.mutateAsync({ email, code });
      setSuccess('Email verified successfully!');
      
      // Step 2: Auto-login in background if password is provided
      const resolvedPassword = password || sessionStorage.getItem(STORAGE_KEYS.SIGNUP_PASSWORD);

      if (resolvedPassword && onVerified) {
        setIsLoggingIn(true);
        try {
          const loginResponse = await loginMutation.mutateAsync({ email, password: resolvedPassword });
          if (loginResponse?.access && loginResponse?.user) {
            login(loginResponse.user, loginResponse.access, loginResponse.refresh);
          }
          // Login successful - tokens are stored automatically by the hook
          // Now proceed to document upload step
          setTimeout(() => {
            setIsLoggingIn(false);
            sessionStorage.removeItem(STORAGE_KEYS.SIGNUP_PASSWORD);
            onVerified();
          }, 500);
        } catch (loginErr) {
          setIsLoggingIn(false);
          // If auto-login fails, still proceed but user will need to login manually
          console.warn('Auto-login failed:', loginErr);
          setError('Auto-login failed. Please sign in to continue.');
        }
      } else if (onVerified) {
        // Session lost, ask user to login manually
        setError('Session expired. Please sign in to continue.');
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 1500);
      } else {
        // No callback, navigate to login
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 1500);
      }
    } catch (err) {
      // Toast notification is handled by the hook, but we can also set local error for form display
      if (err.data?.detail) {
        setError(err.data.detail);
      } else {
        setError('Verification failed. Please check your code and try again.');
      }
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');

    try {
      await resendCodeMutation.mutateAsync(email);
      setSuccess('Verification code has been sent to your email.');
    } catch (err) {
      // Toast notification is handled by the hook, but we can also set local error for form display
      if (err.data?.detail) {
        setError(err.data.detail);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    }
  };

  return (
    <div className="email-verification">
      <div className="email-verification__container">
        <h2 className="email-verification__title">Verify Your Email</h2>
        <p className="email-verification__subtitle">
          We've sent a 6-digit verification code to <strong>{email}</strong>
        </p>
        <p className="email-verification__hint">
          Please check your email and enter the code below. The code expires in 10 minutes.
        </p>

        {error && (
          <div className="email-verification__error">
            {error}
          </div>
        )}

        {success && (
          <div className="email-verification__success">
            {success}
          </div>
        )}

        <form onSubmit={handleVerify} className="email-verification__form">
          <div className="email-verification__code-input">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={handleCodeChange}
              className="email-verification__input"
              placeholder="000000"
              maxLength={6}
              disabled={verifyEmailMutation.isPending}
            />
          </div>

          <div className="email-verification__actions">
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={code.length !== 6 || verifyEmailMutation.isPending || isLoggingIn}
              className="email-verification__button"
            >
              {isLoggingIn 
                ? 'Logging in...' 
                : verifyEmailMutation.isPending 
                  ? 'Verifying...' 
                  : 'Verify Email'}
            </Button>
          </div>
        </form>

        <div className="email-verification__resend">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCodeMutation.isPending}
            className="email-verification__resend-button"
          >
            {resendCodeMutation.isPending ? 'Sending...' : 'Resend Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;

